import React from "react";
import { Link } from "react-router-dom";
import { useAuth, isProductionForeman } from "../../context/AuthContext";
import { useDepartmentRuntime } from "../../hooks/useDepartmentRuntime";
import { useBoilerWorkspace } from "../../hooks/useBoilerWorkspace";
import { useForemanTeam } from "../../hooks/useForemanTeam";
import { PageHeader, KpiTile, SectionCard, Pill } from "../../components/Ui";
import RuntimeCounter from "../../components/RuntimeCounter";
import { RuntimeStatusBadge } from "../../components/production/RuntimeShared";
import { Button } from "../../components/ui/button";
import { INDIA_LOCALE, INDIA_TIME_ZONE } from "../../lib/indiaTime";
import { ArrowRight, ExternalLink, Flame, Users, Droplets, Factory } from "lucide-react";

export const DEPARTMENT_META = {
  boiler: {
    label: "Boiler Department",
    eyebrow: "Production · Boiler",
    title: "Boiler Dashboard",
    description: "Thermax combipack status, operator shift, husk beds, and blowdown.",
    workspacePath: "/production/boiler",
    dashboardPath: "/production/boiler/dashboard",
    icon: Flame,
  },
  parboiled: {
    label: "Parboiled Department",
    eyebrow: "Production · Parboiled",
    title: "Parboiled Dashboard",
    description: "Steaming and dryer sections — live runtime and recent activity.",
    workspacePath: "/production/parboiled",
    dashboardPath: "/production/parboiled/dashboard",
    icon: Droplets,
  },
  milling: {
    label: "Milling Department",
    eyebrow: "Production · Milling",
    title: "Milling Dashboard",
    description: "Milling line status — de-huskers, polishers, and sorters.",
    workspacePath: "/production/milling",
    dashboardPath: "/production/milling/dashboard",
    icon: Factory,
  },
};

function formatDueLabel(hourlyCheck) {
  if (!hourlyCheck) return null;
  if (hourlyCheck.overdue) return "Hourly check overdue";
  const sec = hourlyCheck.due_in_seconds;
  if (sec == null) return null;
  const mins = Math.ceil(sec / 60);
  return `Hourly check in ${mins} min`;
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatBedHours(hours) {
  if (hours == null || Number.isNaN(hours)) return "0.0";
  return Number(hours).toFixed(1);
}

function formatLogTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(INDIA_LOCALE, {
    timeZone: INDIA_TIME_ZONE,
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BoilerDashboardBody({ meta, isForeman }) {
  const { data: workspace, runtime, hourlyCheck, bedTracking, blowdown } = useBoilerWorkspace();

  if (!workspace) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading combipack status…</p>;
  }

  const operatorShift = workspace.operator_shift;
  const blowdownActive = blowdown?.active?.is_active;
  const dueLabel = formatDueLabel(hourlyCheck);
  const isRunning = runtime?.status === "RUNNING";
  const lastBlowdown = blowdown?.last_completed;

  return (
    <>
      <SectionCard title="Thermax 10-Ton Combipack" subtitle="Live operator workspace" testId="dept-boiler-live">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Status</p>
            <div className="flex flex-wrap items-center gap-3">
              <Pill tone={isRunning ? "success" : "default"}>{runtime?.status || "STOPPED"}</Pill>
              {isRunning && (
                <RuntimeCounter
                  startTime={runtime.start_time}
                  running
                  showVerbose={false}
                  clockClassName="font-mono text-lg tabular-nums text-primary"
                />
              )}
            </div>
            {isRunning && runtime?.started_by_name && (
              <p className="text-xs text-muted-foreground mt-2">Started by {runtime.started_by_name}</p>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Current operator</p>
            <p className="font-medium">{operatorShift?.current_operator_name || "—"}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Hourly check:{" "}
              <span className={dueLabel?.includes("overdue") ? "text-red-700 font-medium" : ""}>
                {dueLabel || (isRunning ? "—" : "Start boiler first")}
              </span>
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Blowdown</p>
            <p className="font-medium">{blowdownActive ? "In progress" : "Idle"}</p>
            {lastBlowdown && (
              <p className="text-xs text-muted-foreground mt-2">
                Last {formatDuration(lastBlowdown.duration_seconds)} · {formatLogTime(lastBlowdown.started_at)}
              </p>
            )}
          </div>
        </div>

        {isRunning && (
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-border/60">
            <div className="rounded-lg border px-3 py-2.5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Bed 1 active</p>
              <p className="font-mono text-lg tabular-nums text-primary mt-1">
                {formatBedHours(bedTracking?.bed1_active_hours)}h
              </p>
              <Pill tone={bedTracking?.bed1_running ? "success" : "default"} className="mt-2">
                {bedTracking?.bed1_running ? "Feeding" : "Stopped"}
              </Pill>
            </div>
            <div className="rounded-lg border px-3 py-2.5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Bed 2 active</p>
              <p className="font-mono text-lg tabular-nums text-primary mt-1">
                {formatBedHours(bedTracking?.bed2_active_hours)}h
              </p>
              <Pill tone={bedTracking?.bed2_running ? "success" : "default"} className="mt-2">
                {bedTracking?.bed2_running ? "Feeding" : "Stopped"}
              </Pill>
            </div>
          </div>
        )}

        <Link to={meta.workspacePath} className="inline-flex items-center gap-1 text-xs link-brand mt-4">
          Open boiler workspace <ArrowRight className="h-3 w-3" />
        </Link>
        <Link to="/production/boiler/register" className="inline-flex items-center gap-1 text-xs link-brand mt-2 ml-4">
          View log register <ArrowRight className="h-3 w-3" />
        </Link>
      </SectionCard>

      {isForeman && <ForemanOperatorsSection department="boiler" />}
    </>
  );
}

function ForemanTeamKpi({ department }) {
  const { operators } = useForemanTeam();
  const onShift = operators.filter((o) => o.on_shift).length;
  return (
    <KpiTile
      label="On shift"
      value={onShift}
      sub={`${operators.length} operator${operators.length === 1 ? "" : "s"} on team`}
      accent
      testId={`${department}-kpi-team`}
      icon={Users}
    />
  );
}

function ForemanOperatorsSection({ department = "boiler" }) {
  const { operators } = useForemanTeam();
  if (!operators.length) return null;

  return (
    <SectionCard title="Operators today" testId={`${department}-operators`}>
      <div className="space-y-2">
        {operators.map((op) => (
          <div key={op.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2.5">
            <div>
              <p className="font-medium text-sm">{op.name}</p>
              <p className="text-xs text-muted-foreground">{op.email}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Pill tone={op.is_active !== false ? "success" : "warning"}>{op.is_active !== false ? "Active" : "Disabled"}</Pill>
              {op.is_current_operator && <Pill>Controlling</Pill>}
              {op.on_shift && !op.is_current_operator && <Pill tone="info">On shift</Pill>}
            </div>
          </div>
        ))}
      </div>
      <Link to="/production/team" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
        Manage team <ArrowRight className="h-3 w-3" />
      </Link>
    </SectionCard>
  );
}

function RuntimeDepartmentBody({ department, meta, isForeman }) {
  const { sections, recentLogs, summary, loading } = useDepartmentRuntime(department);
  const Icon = meta.icon;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiTile label="Running" value={summary.running_count ?? 0} accent sub="Sections active" testId={`${department}-kpi-running`} icon={Icon} />
        <KpiTile label="Stopped" value={summary.stopped_count ?? 0} sub="Idle sections" testId={`${department}-kpi-stopped`} />
        <KpiTile label="Breakdown" value={summary.breakdown_count ?? 0} sub="Needs attention" testId={`${department}-kpi-breakdown`} />
        {isForeman ? (
          <ForemanTeamKpi department={department} />
        ) : (
          <KpiTile label="Sections" value={summary.total_sections ?? 0} sub={meta.label} testId={`${department}-kpi-sections`} />
        )}
      </div>

      <SectionCard title="Section status" subtitle="Live runtime for each machine section" testId={`${department}-sections`}>
        {loading && !sections.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : sections.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No sections configured.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {sections.map((section) => (
              <Link
                key={section.slug}
                to={meta.workspacePath}
                className={`rounded-xl border p-4 block transition-colors hover:bg-accent/60 ${section.status === "RUNNING" ? "border-bansal-sage/40 bg-bansal-sage/5" : "border-border"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm leading-snug">{section.name}</p>
                  <RuntimeStatusBadge status={section.status} />
                </div>
                <div className="mt-3">
                  <RuntimeCounter
                    startTime={section.start_time}
                    running={section.status === "RUNNING"}
                    showVerbose={false}
                    clockClassName="font-mono text-lg tabular-nums text-primary"
                  />
                </div>
                {section.started_by_name && (
                  <p className="text-xs text-muted-foreground mt-2 truncate">Started by {section.started_by_name}</p>
                )}
              </Link>
            ))}
          </div>
        )}
      </SectionCard>

      {isForeman && department !== "boiler" && <ForemanOperatorsSection department={department} />}

      <SectionCard title="Recent activity" testId={`${department}-recent-logs`}>
        {recentLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No runtime logs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-3">Section</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Started</th>
                  <th className="py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.slice(0, 8).map((log) => (
                  <tr key={log.id} className="border-b border-border/50">
                    <td className="py-2 pr-3">{log.section_name || log.section_slug}</td>
                    <td className="py-2 pr-3"><RuntimeStatusBadge status={log.status} /></td>
                    <td className="py-2 pr-3 tabular-nums">{formatLogTime(log.start_time)}</td>
                    <td className="py-2">{log.started_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </>
  );
}

export default function DepartmentDashboard({ department }) {
  const meta = DEPARTMENT_META[department];
  const { user } = useAuth();
  const isForeman = isProductionForeman(user);

  if (!meta) {
    return <div className="py-16 text-center text-muted-foreground">Unknown department</div>;
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid={`${department}-department-dashboard`}>
      <PageHeader
        eyebrow={meta.eyebrow}
        title={meta.title}
        description={meta.description}
        actions={(
          <div className="flex flex-col sm:flex-row gap-2">
            <Button asChild variant="outline" className="min-h-11">
              <Link to={meta.workspacePath}>
                <span className="inline-flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open workspace
                </span>
              </Link>
            </Button>
            {isForeman && (
              <Button asChild className="min-h-11 bg-bansal-sage hover:bg-bansal-sageDark text-white">
                <Link to="/production/team">
                  <span className="inline-flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    My team
                  </span>
                </Link>
              </Button>
            )}
          </div>
        )}
      />

      {department === "boiler" ? (
        <BoilerDashboardBody meta={meta} isForeman={isForeman} />
      ) : (
        <RuntimeDepartmentBody department={department} meta={meta} isForeman={isForeman} />
      )}
    </div>
  );
}
